import React, { useState, useEffect, useContext, useRef } from "react";
import Navbar from "../components/common/Navbar";
import DocumentsPageHeader from "../components/common/DocumentsPageHeader";
import getAllEmailTemplates from "../api/getAllEmailTemplates";
import { AuthContext } from "../context/Auth/AuthContext";
import baseUrl from "../config/baseUrl";
import AddEmailTemplate from "../components/email-templates/AddEmailTemplate";
import EditEmailTemplate from "../components/email-templates/EditEmailTemplate";
import EmailTemplateStatusPopup from "../components/email-templates/EmailTemplateStatusPopup";
import DeleteEmailTemplatePopup from "../components/email-templates/DeleteEmailTemplatePopup";
import BulkDeleteEmailTemplatePopup from "../components/email-templates/BulkDeleteEmailTemplatePopup";
import ButtonUI from "../components/material-ui/ButtonUI";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import "../styles/Users.css";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";

import { Button } from "../shadcn-components/ui/button";
import { Checkbox } from "../shadcn-components/ui/checkbox";

import {
  CaretSortIcon,
  ChevronDownIcon,
  MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn-components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../shadcn-components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn-components/ui/select";
import { Input } from "../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../shadcn-components/ui/table";

import TableSkeleton from "../components/common/TableSkeleton";
import PaginationSection from "../components/common/PaginationSection";

import { useDebounce } from "use-debounce";
import { useRoleContext } from "../context/RolesContext";
import { formatDate } from "../utils/formatDate";
import { getEmailTemplateTypeLabel } from "../utils/emailTemplateOptions";
import { EditActionButton } from "../components/common/ActionIconButton";

const EmailTemplates = () => {
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [emailTemplatesAdded, setEmailTemplatesAdded] = useState(0);

  const { token, user_id, is_admin } = useContext(AuthContext);
  const [emailTemplatesResponse, setEmailTemplatesResponse] = useState([]);
  const [bulkDeleteEmailTemplate, setBulkDeleteEmailTemplate] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState();

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const prevFilterParamsRef = useRef({});
  const prevStateRef = useRef({
    createdAt,
    updatedAt,
    debouncedGlobalFilter,
    columnFilters,
  });

  // Role based permissions
  const { permissions } = useRoleContext();
  const hasPermission = (permission) => {
    if (is_admin === "1") {
      return true;
    }
    return permissions.includes(permission);
  };

  const canAddEmailTemplate = hasPermission("create_email_templates");
  const canEditEmailTemplate = hasPermission("edit_email_templates");
  const canDeleteEmailTemplate = hasPermission("delete_email_templates");
  const shouldShowActionColumn = canEditEmailTemplate || canDeleteEmailTemplate;

  const fetchEmailTemplates = async () => {
    setLoading(true);
    const offset = currentPage - 1;
    const filterParams = {};

    columnFilters.forEach((filterItem) => {
      filterParams[filterItem.id] = filterItem.value;
    });

    const filtersChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);

    if (filtersChanged) {
      setCurrentPage(1);
    }

    prevFilterParamsRef.current = filterParams;

    const sortParam = sorting
      .map((sortItem) => {
        return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
      })
      .join(",");

    const params = {
      isAdmin: is_admin,
      userId: user_id,
      limit: pageSize,
      offset: offset,
      ...filterParams,
      global_search: globalFilter,
      sort: sortParam,
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${baseUrl}/api/get-all-email-template-data?${queryString}`;

    try {
      const response = await getAllEmailTemplates({ token, api: url });

      if (response.code === 200) {
        setTotalPages(Math.ceil(response?.data[0].allCasesCount / pageSize));
        setEmailTemplatesResponse(response?.data[0].cases);
      } else {
        setTotalPages(0);
        setEmailTemplatesResponse([]);
      }
    } catch (error) {
      console.error("Error fetching email templates data:", error);
      setTotalPages(0);
      setEmailTemplatesResponse([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentState = {
      createdAt,
      updatedAt,
      debouncedGlobalFilter,
      columnFilters,
    };

    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    prevStateRef.current = currentState;
    fetchEmailTemplates();
  }, [
    emailTemplatesAdded,
    debouncedGlobalFilter,
    currentPage,
    columnFilters,
    sorting,
    pageSize,
    createdAt,
    updatedAt,
  ]);

  const onAddButtonClick = () => {
    setShowAddForm(!showAddForm);
  };

  const setEmailTemplatesAddedToTrue = () => {
    setEmailTemplatesAdded((count) => count + 1);
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    const ids = selectedRowsData.map((row) => row.id).join(",");
    if (actionType === "delete") {
      setBulkDeleteIds(ids);
      setBulkDeleteEmailTemplate(true);
    }
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() ||
            table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon className="" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("delete")}
                  disabled={!canDeleteEmailTemplate}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            setSelectAllChecked(true);
            row.toggleSelected(!!value);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      displayName: "Template Name",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Template Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("name")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "type",
      displayName: "Type",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Type
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">
          {getEmailTemplateTypeLabel(row.getValue("type"))}
          {row.original.language
            ? ` (${row.original.language.toUpperCase()})`
            : ""}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "subject",
      displayName: "Subject",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Subject
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div>{row.getValue("subject")}</div>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "status",
      displayName: "Status",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Status
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">
          {row.getValue("status")?.toLowerCase() === "active"
            ? "Active"
            : "Inactive"}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "created_at",
      displayName: "Created At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Created At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const createdAtVal = row.getValue("created_at");
        return (
          <div className="capitalize text-wrap">{formatDate(createdAtVal)}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "updated_at",
      displayName: "Modified At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Modified At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const updated = row.getValue("updated_at");
        return (
          <div className="capitalize text-wrap">{formatDate(updated)}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "actions",
      displayName: "Actions",
      header: ({ column }) => {
        if (!shouldShowActionColumn) {
          return null;
        }
        return (
          <Button variant="ghost" className="p-0 !cursor-default">
            Actions
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="flex gap-1.5">
          {canEditEmailTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <EmailTemplateStatusPopup
                      setEmailTemplatesAddedToTrue={
                        setEmailTemplatesAddedToTrue
                      }
                      tableValues={row.original}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Change Status</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canEditEmailTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <EditActionButton onClick={() => setEditingTemplate(row.original)} />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Edit</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {canDeleteEmailTemplate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span>
                    <DeleteEmailTemplatePopup
                      setEmailTemplatesAddedToTrue={
                        setEmailTemplatesAddedToTrue
                      }
                      id={row.original.id}
                      setRowSelection={setRowSelection}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
      enableHiding: false,
    },
  ];

  const table = useReactTable({
    data: emailTemplatesResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pageSize: 20,
    },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue);
    },
  });

  return (
    <>
      {bulkDeleteEmailTemplate ? (
        <BulkDeleteEmailTemplatePopup
          bulkIds={bulkDeleteIds}
          bulkDeleteEmailTemplate={bulkDeleteEmailTemplate}
          setBulkDeleteEmailTemplate={setBulkDeleteEmailTemplate}
          setRowSelection={setRowSelection}
          setEmailTemplatesAddedToTrue={setEmailTemplatesAddedToTrue}
        />
      ) : null}
      <div className="user-container">
        <div className="navbar">
          <Navbar />
        </div>
        <div className="user-table">
          {showAddForm ? (
            <AddEmailTemplate
              onAddButtonClick={onAddButtonClick}
              setEmailTemplatesAddedToTrue={setEmailTemplatesAddedToTrue}
            />
          ) : editingTemplate ? (
            <EditEmailTemplate
              tableValues={editingTemplate}
              onEditButtonClick={() => setEditingTemplate(null)}
              setEmailTemplatesAddedToTrue={setEmailTemplatesAddedToTrue}
            />
          ) : (
            <div className="user-content settings-content">
              <DocumentsPageHeader
                showBreadcrumb={false}
                breadcrumbs={[{ label: "Administration" }, { label: "Email Templates" }]}
                icon={DraftsOutlinedIcon}
                title="Email Templates"
                description="Manage reusable email templates"
                action={canAddEmailTemplate ? (
                  <button className="add-document" onClick={onAddButtonClick}>
                    <NoteAddIcon sx={{ fontSize: 18 }} />
                    Add Email Template
                  </button>
                ) : null}
              />
              <div className="user-table-content">
                <div className="table-content">
                  <div className="document__table mt-3">
                    <div className="w-full bg-white px-2">
                      <div className="flex justify-between items-center py-3 flex-wrap gap-4">
                        <Input
                          placeholder="Search..."
                          value={globalFilter}
                          onChange={(event) =>
                            table.setGlobalFilter(event.target.value)
                          }
                          className="max-w-sm"
                        />
                        <div className="flex gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" className="ml-auto">
                                Columns{" "}
                                <ChevronDownIcon className="ml-2 h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                  return (
                                    <DropdownMenuCheckboxItem
                                      key={column.id}
                                      className="capitalize"
                                      checked={column.getIsVisible()}
                                      onCheckedChange={(value) =>
                                        column.toggleVisibility(!!value)
                                      }
                                    >
                                      {column.columnDef.displayName}
                                    </DropdownMenuCheckboxItem>
                                  );
                                })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <div className="rounded-md border">
                        {loading ? (
                          <TableSkeleton />
                        ) : (
                          <Table>
                            <TableHeader className="pb-4 sticky top-0 bg-white z-[1]">
                              {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                  {headerGroup.headers.map((header) => {
                                    return (
                                      <TableHead key={header.id}>
                                        {header.isPlaceholder
                                          ? null
                                          : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext()
                                            )}
                                      </TableHead>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableHeader>
                            <TableBody>
                              {table?.getRowModel().rows?.length > 0 ? (
                                table?.getRowModel().rows.map((row) => (
                                  <TableRow
                                    key={row.id}
                                    data-state={
                                      row.getIsSelected() && "selected"
                                    }
                                  >
                                    {row.getVisibleCells().map((cell) => (
                                      <TableCell key={cell.id}>
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        )}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                  >
                                    {loading ? (
                                      <TableSkeleton />
                                    ) : (
                                      "no records found"
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                      <div className="flex items-center justify-between space-x-2 py-1 flex-wrap gap-4">
                        <div className="flex items-center gap-5">
                          <div className="flex-1 text-sm text-muted-foreground">
                            {table.getFilteredSelectedRowModel().rows.length} of{" "}
                            {table.getFilteredRowModel().rows.length} row(s)
                            selected.
                          </div>
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium">Rows per page</p>
                            <Select
                              value={`${table.getState().pageSize}`}
                              onValueChange={(value) => {
                                setPageSize(Number(value));
                                setCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-auto">
                                <SelectValue
                                  placeholder={table.getState().pageSize}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {[10, 20, 30, 40, 50].map((size) => (
                                    <SelectItem key={size} value={`${size}`}>
                                      {size}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-x-2">
                          <PaginationSection
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            currentPage={currentPage}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailTemplates;
